// =============================================================================
// reportDatasets.ts — T-392
// Registry powering the Reports hub + generic Data Explorer. One entry per
// dataset James can filter + export. RULES:
//   - Every `select` string is copied from a PROVEN working screen query (file
//     noted per dataset) — do not "improve" them blind; column spellings vary
//     (ap_invoices amount_inc_gst / client_invoices total_incl_gst /
//     purchase_orders total_inc_gst) and some embeds need FK disambiguation.
//   - Where a screen hydrates lookups client-side instead of embedding
//     (quotes, POs, AP → projects), we keep that pattern via `hydrate`.
//   - The explorer gates manager+; RLS is the real enforcement underneath.
// =============================================================================

import type { LucideIcon } from "lucide-react";
import {
  FolderKanban, Target, FileText, Receipt, FileSpreadsheet, Wallet,
  Clock, BookOpen, Camera, ShoppingCart, BadgeCheck, HardHat,
  Truck, Repeat, Users, TrendingUp,
} from "lucide-react";
import { supabase } from "@/lib/supabase";

// -----------------------------------------------------------------------------

export type ColType = "text" | "date" | "datetime" | "money" | "num" | "badge" | "bool";

export interface ReportColumn {
  key:   string;   // key into the flattened row
  label: string;
  type:  ColType;
}

export interface StatusOption { value: string; label: string }

export interface ReportDataset {
  id:      string;
  label:   string;
  blurb:   string;
  icon:    LucideIcon;
  group:   "Commercial" | "Money in / out" | "Delivery" | "People & compliance";
  table:   string;
  select:  string;
  /** top-level columns for the or-ilike search; empty = no search box */
  searchFields: string[];
  /** optional: ilike search over an INNER-joined embedded resource (e.g. employee name) */
  searchEmbed?: { table: string; fields: string[] };
  /** column for the from/to date-range filter; undefined = no range filter */
  dateField?:  { key: string; label: string };
  /** single-select filter (status / state / frequency…) */
  statusField?: { key: string; label: string; isBool?: boolean; options: StatusOption[] };
  defaultOrder: { field: string; asc: boolean };
  columns: ReportColumn[];
  /** flatten a raw supabase row (embeds and all) into export-ready cells */
  flatten: (row: any) => Record<string, unknown>;
  /** optional client-side lookup hydration (mirrors screens that avoid embeds) */
  hydrate?: (rows: any[]) => Promise<any[]>;
}

// -----------------------------------------------------------------------------
// Shared hydration helpers (the .in() lookup pattern proven on the source screens)
// -----------------------------------------------------------------------------

async function stampProjects(rows: any[], idKey = "project_id"): Promise<any[]> {
  const pids = [...new Set(rows.map(r => r[idKey]).filter(Boolean))];
  if (pids.length === 0) return rows;
  const { data } = await supabase
    .from("projects")
    .select("project_id, project_code, project_name")
    .in("project_id", pids);
  const map = new Map((data ?? []).map((p: any) => [p.project_id, p]));
  return rows.map(r => ({ ...r, _project: map.get(r[idKey]) ?? null }));
}

async function stampClients(rows: any[], idKey = "client_id"): Promise<any[]> {
  const ids = [...new Set(rows.map(r => r[idKey]).filter(Boolean))];
  if (ids.length === 0) return rows;
  const { data } = await supabase
    .from("clients")
    .select("client_id, legal_name")
    .in("client_id", ids);
  const map = new Map((data ?? []).map((c: any) => [c.client_id, c]));
  return rows.map(r => ({ ...r, _client: map.get(r[idKey]) ?? null }));
}

async function stampSuppliers(rows: any[], idKey = "supplier_id"): Promise<any[]> {
  const ids = [...new Set(rows.map(r => r[idKey]).filter(Boolean))];
  if (ids.length === 0) return rows;
  const { data } = await supabase
    .from("suppliers")
    .select("supplier_id, legal_name")
    .in("supplier_id", ids);
  const map = new Map((data ?? []).map((s: any) => [s.supplier_id, s]));
  return rows.map(r => ({ ...r, _supplier: map.get(r[idKey]) ?? null }));
}

async function stampEmployees(rows: any[], idKey = "employee_id"): Promise<any[]> {
  const ids = [...new Set(rows.map(r => r[idKey]).filter(Boolean))];
  if (ids.length === 0) return rows;
  const { data } = await supabase
    .from("v_employees")
    .select("employee_id, first_name, last_name, employment_type")
    .in("employee_id", ids);
  const map = new Map((data ?? []).map((e: any) => [e.employee_id, e]));
  return rows.map(r => ({ ...r, _employee: map.get(r[idKey]) ?? null }));
}

const empName = (e: any) =>
  e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() : "";

const num = (v: unknown) => (v == null || v === "" ? null : Number(v));

// -----------------------------------------------------------------------------
// The registry
// -----------------------------------------------------------------------------

const PROJECT_STATUSES: StatusOption[] = [
  "bid", "quoted", "won", "scheduled", "mobilising", "live", "on_hold",
  "acceptance_pending", "closed", "cancelled", "lost",
].map(s => ({ value: s, label: s.replace(/_/g, " ") }));

export const REPORT_DATASETS: ReportDataset[] = [
  // ------------------------------------------------------------- Commercial
  {
    id: "projects",
    label: "Projects",
    blurb: "Every project with client, carrier, site, dates and contract value.",
    icon: FolderKanban,
    group: "Commercial",
    table: "projects",
    // proven: ProjectsListScreen.tsx
    select: `project_id, project_code, project_name, status, contract_value,
      scheduled_start_date, scheduled_end_date, risk_rating,
      clients(legal_name, client_code),
      carriers(carrier_code),
      sites(site_code, site_name, suburb),
      pm:employees!projects_project_manager_id_fkey(first_name, last_name)`,
    searchFields: ["project_code", "project_name"],
    dateField: { key: "scheduled_start_date", label: "Scheduled start" },
    statusField: { key: "status", label: "Status", options: PROJECT_STATUSES },
    defaultOrder: { field: "project_code", asc: false },
    columns: [
      { key: "Code", label: "Code", type: "text" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Client", label: "Client", type: "text" },
      { key: "Carrier", label: "Carrier", type: "text" },
      { key: "Site", label: "Site", type: "text" },
      { key: "Start", label: "Start", type: "date" },
      { key: "End", label: "End", type: "date" },
      { key: "Contract value", label: "Contract value", type: "money" },
      { key: "PM", label: "PM", type: "text" },
    ],
    flatten: (r) => ({
      "Code": r.project_code,
      "Project": r.project_name,
      "Status": r.status,
      "Client": r.clients?.legal_name ?? "",
      "Carrier": r.carriers?.carrier_code ?? "",
      "Site": r.sites ? `${r.sites.site_name ?? ""}${r.sites.suburb ? " · " + r.sites.suburb : ""}` : "",
      "Start": r.scheduled_start_date,
      "End": r.scheduled_end_date,
      "Contract value": num(r.contract_value),
      "PM": empName(r.pm),
    }),
  },
  {
    id: "project_financials",
    label: "Project financials",
    blurb: "Contract value, invoiced, paid, spend and margin per project.",
    icon: TrendingUp,
    group: "Commercial",
    table: "v_project_financials",
    select: "*", // proven: CommercialDashboardScreen.tsx (view has no date column)
    searchFields: ["project_code", "project_name"],
    statusField: { key: "status", label: "Status", options: PROJECT_STATUSES },
    defaultOrder: { field: "contract_value", asc: false },
    columns: [
      { key: "Code", label: "Code", type: "text" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Contract value", label: "Contract value", type: "money" },
      { key: "Invoiced", label: "Invoiced", type: "money" },
      { key: "Paid", label: "Paid", type: "money" },
      { key: "AP spend", label: "AP spend", type: "money" },
      { key: "Est. margin", label: "Est. margin", type: "money" },
      { key: "Margin %", label: "Margin %", type: "num" },
    ],
    flatten: (r) => ({
      "Code": r.project_code,
      "Project": r.project_name,
      "Status": r.status,
      "Contract value": num(r.contract_value),
      "Invoiced": num(r.invoiced_total),
      "Paid": num(r.paid_total),
      "AP spend": num(r.ap_spend),
      "Est. margin": num(r.estimated_margin),
      "Margin %": num(r.estimated_margin_percent),
    }),
  },
  {
    id: "bids",
    label: "BIDs",
    blurb: "Tender pipeline — status, due dates, estimated values.",
    icon: Target,
    group: "Commercial",
    table: "bids",
    // proven: BidsScreens.tsx list
    select: `bid_id, bid_number, bid_title, client_ref, status,
      due_date, estimated_value, created_at,
      clients(legal_name, client_code),
      carriers(carrier_code),
      sites(site_code)`,
    searchFields: ["bid_number", "bid_title", "client_ref"],
    dateField: { key: "due_date", label: "Due date" },
    statusField: {
      key: "status", label: "Status",
      options: ["open", "responded", "won", "lost", "no_bid", "withdrawn"]
        .map(s => ({ value: s, label: s.replace(/_/g, " ") })),
    },
    defaultOrder: { field: "due_date", asc: false },
    columns: [
      { key: "BID #", label: "BID #", type: "text" },
      { key: "Title", label: "Title", type: "text" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Client", label: "Client", type: "text" },
      { key: "Client ref", label: "Client ref", type: "text" },
      { key: "Carrier", label: "Carrier", type: "text" },
      { key: "Site", label: "Site", type: "text" },
      { key: "Due", label: "Due", type: "date" },
      { key: "Est. value", label: "Est. value", type: "money" },
    ],
    flatten: (r) => ({
      "BID #": r.bid_number,
      "Title": r.bid_title,
      "Status": r.status,
      "Client": r.clients?.legal_name ?? "",
      "Client ref": r.client_ref ?? "",
      "Carrier": r.carriers?.carrier_code ?? "",
      "Site": r.sites?.site_code ?? "",
      "Due": r.due_date,
      "Est. value": num(r.estimated_value),
    }),
  },
  {
    id: "quotes",
    label: "Quotes",
    blurb: "Every quote and revision with totals, margin and status.",
    icon: FileText,
    group: "Commercial",
    table: "quotes",
    // proven: QuotesListScreen.tsx (deliberately no embeds — hydrated below)
    select: `quote_id, quote_number, quote_title, status, quote_date, valid_until,
      total_incl_gst, margin_percent, client_id, project_id, bid_id,
      revision_number, parent_quote_id, quote_type`,
    searchFields: ["quote_number", "quote_title"],
    dateField: { key: "quote_date", label: "Quote date" },
    statusField: {
      key: "status", label: "Status",
      options: ["draft", "pending_approval", "sent", "accepted", "rejected",
        "expired", "withdrawn", "partially_invoiced", "invoiced", "superseded"]
        .map(s => ({ value: s, label: s.replace(/_/g, " ") })),
    },
    defaultOrder: { field: "quote_date", asc: false },
    hydrate: async (rows) => stampProjects(await stampClients(rows)),
    columns: [
      { key: "Quote #", label: "Quote #", type: "text" },
      { key: "Title", label: "Title", type: "text" },
      { key: "Type", label: "Type", type: "badge" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Client", label: "Client", type: "text" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Date", label: "Date", type: "date" },
      { key: "Total inc GST", label: "Total inc GST", type: "money" },
      { key: "Margin %", label: "Margin %", type: "num" },
    ],
    flatten: (r) => ({
      "Quote #": `${r.quote_number}${r.revision_number ? `-R${r.revision_number}` : ""}`,
      "Title": r.quote_title,
      "Type": r.quote_type ?? "standard",
      "Status": r.status,
      "Client": r._client?.legal_name ?? "",
      "Project": r._project?.project_code ?? "",
      "Date": r.quote_date,
      "Total inc GST": num(r.total_incl_gst),
      "Margin %": num(r.margin_percent),
    }),
  },

  // --------------------------------------------------------- Money in / out
  {
    id: "ap_invoices",
    label: "Bills (AP)",
    blurb: "Supplier bills — amounts, status, due dates, reimbursements.",
    icon: Receipt,
    group: "Money in / out",
    table: "ap_invoices",
    // proven: ApXeroScreen.tsx (amount_inc_gst spelling!)
    select: `ap_invoice_id, invoice_number, supplier_name, invoice_date, due_date,
      paid_date, amount_ex_gst, gst_amount, amount_inc_gst, budget_code, payment_method,
      project_id, description, status, is_reimbursement, capture_source, captured_at`,
    searchFields: ["supplier_name", "invoice_number", "description"],
    dateField: { key: "invoice_date", label: "Invoice date" },
    statusField: {
      key: "status", label: "Status",
      options: ["captured", "approved", "entered_in_xero", "paid", "disputed", "void"]
        .map(s => ({ value: s, label: s === "entered_in_xero" ? "in Xero (to pay)" : s })),
    },
    defaultOrder: { field: "invoice_date", asc: false },
    hydrate: (rows) => stampProjects(rows),
    columns: [
      { key: "Supplier", label: "Supplier", type: "text" },
      { key: "Invoice #", label: "Invoice #", type: "text" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Invoice date", label: "Invoice date", type: "date" },
      { key: "Due", label: "Due", type: "date" },
      { key: "Paid", label: "Paid", type: "date" },
      { key: "Ex GST", label: "Ex GST", type: "money" },
      { key: "GST", label: "GST", type: "money" },
      { key: "Inc GST", label: "Inc GST", type: "money" },
      { key: "Budget code", label: "Budget code", type: "text" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Reimbursement", label: "Reimbursement", type: "bool" },
      { key: "Description", label: "Description", type: "text" },
    ],
    flatten: (r) => ({
      "Supplier": r.supplier_name ?? "",
      "Invoice #": r.invoice_number ?? "",
      "Status": r.status,
      "Invoice date": r.invoice_date,
      "Due": r.due_date,
      "Paid": r.paid_date,
      "Ex GST": num(r.amount_ex_gst),
      "GST": num(r.gst_amount),
      "Inc GST": num(r.amount_inc_gst),
      "Budget code": r.budget_code ?? "",
      "Project": r._project?.project_code ?? "",
      "Reimbursement": !!r.is_reimbursement,
      "Description": r.description ?? "",
    }),
  },
  {
    id: "client_invoices",
    label: "Invoices (AR)",
    blurb: "Client invoices — issued, paid, part-paid and outstanding.",
    icon: FileSpreadsheet,
    group: "Money in / out",
    table: "client_invoices",
    // proven: InvoicesScreens.tsx list (total_incl_gst spelling!)
    select: `invoice_id, invoice_number, total_incl_gst, amount_paid, invoice_date,
      due_date, status, sent_at, paid_at,
      projects ( project_code, project_name, sites ( site_name ) ),
      clients  ( legal_name, trading_name )`,
    searchFields: ["invoice_number"],
    dateField: { key: "invoice_date", label: "Invoice date" },
    statusField: {
      key: "status", label: "Status",
      options: ["draft", "pending_approval", "approved", "sent", "part_paid",
        "paid", "overdue", "void"]
        .map(s => ({ value: s, label: s.replace(/_/g, " ") })),
    },
    defaultOrder: { field: "invoice_date", asc: false },
    columns: [
      { key: "Invoice #", label: "Invoice #", type: "text" },
      { key: "Client", label: "Client", type: "text" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Site", label: "Site", type: "text" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Invoice date", label: "Invoice date", type: "date" },
      { key: "Due", label: "Due", type: "date" },
      { key: "Total inc GST", label: "Total inc GST", type: "money" },
      { key: "Paid", label: "Paid", type: "money" },
      { key: "Outstanding", label: "Outstanding", type: "money" },
      { key: "Paid at", label: "Paid at", type: "date" },
    ],
    flatten: (r) => ({
      "Invoice #": r.invoice_number,
      "Client": r.clients?.legal_name ?? "",
      "Project": r.projects?.project_code ?? "",
      "Site": r.projects?.sites?.site_name ?? "",
      "Status": r.status,
      "Invoice date": r.invoice_date,
      "Due": r.due_date,
      "Total inc GST": num(r.total_incl_gst),
      "Paid": num(r.amount_paid) ?? 0,
      "Outstanding": (num(r.total_incl_gst) ?? 0) - (num(r.amount_paid) ?? 0),
      "Paid at": r.paid_at ? String(r.paid_at).slice(0, 10) : null,
    }),
  },
  {
    id: "budget_spend",
    label: "Project spend detail",
    blurb: "Every spend line across all projects — actuals, commitments, source.",
    icon: Wallet,
    group: "Money in / out",
    table: "budget_spend",
    // proven: projectFinancialsExport.ts + project_id (column exists, mig 166)
    select: `project_id, budget_code, spend_type, amount_ex_gst, spend_date,
      reference_no, description, status, supplier_name_cache,
      source_record_type, source_record_id`,
    searchFields: ["description", "supplier_name_cache", "reference_no", "budget_code"],
    dateField: { key: "spend_date", label: "Spend date" },
    statusField: {
      key: "status", label: "Status",
      options: [{ value: "open", label: "open" }, { value: "paid", label: "paid" }],
    },
    defaultOrder: { field: "spend_date", asc: false },
    hydrate: (rows) => stampProjects(rows),
    columns: [
      { key: "Project", label: "Project", type: "text" },
      { key: "Budget code", label: "Budget code", type: "text" },
      { key: "Date", label: "Date", type: "date" },
      { key: "Supplier", label: "Supplier", type: "text" },
      { key: "Description", label: "Description", type: "text" },
      { key: "Ex GST", label: "Ex GST", type: "money" },
      { key: "Kind", label: "Kind", type: "badge" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Source", label: "Source", type: "text" },
      { key: "Ref", label: "Ref", type: "text" },
    ],
    flatten: (r) => ({
      "Project": r._project?.project_code ?? "",
      "Budget code": r.budget_code ?? "",
      "Date": r.spend_date,
      "Supplier": r.supplier_name_cache ?? "",
      "Description": r.description ?? "",
      "Ex GST": num(r.amount_ex_gst),
      "Kind": r.spend_type === "committed" ? "committed" : "actual",
      "Status": r.status ?? "open",
      "Source": r.source_record_type ?? "manual",
      "Ref": r.reference_no ?? "",
    }),
  },
  {
    id: "purchase_orders",
    label: "Purchase orders",
    blurb: "POs raised — status, supplier, project and totals.",
    icon: ShoppingCart,
    group: "Money in / out",
    table: "purchase_orders",
    // proven: PosScreens.tsx (total_inc_gst spelling!) — hydrated lookups
    select: "po_id, po_number, po_title, status, po_date, total_inc_gst, project_id, supplier_id",
    searchFields: ["po_number", "po_title"],
    dateField: { key: "po_date", label: "PO date" },
    statusField: {
      key: "status", label: "Status",
      options: ["draft", "sent", "acknowledged", "received", "invoiced", "paid"]
        .map(s => ({ value: s, label: s })),
    },
    defaultOrder: { field: "po_date", asc: false },
    hydrate: async (rows) => stampProjects(await stampSuppliers(rows)),
    columns: [
      { key: "PO #", label: "PO #", type: "text" },
      { key: "Title", label: "Title", type: "text" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Supplier", label: "Supplier", type: "text" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Date", label: "Date", type: "date" },
      { key: "Total inc GST", label: "Total inc GST", type: "money" },
    ],
    flatten: (r) => ({
      "PO #": r.po_number,
      "Title": r.po_title ?? "",
      "Status": r.status,
      "Supplier": r._supplier?.legal_name ?? "",
      "Project": r._project?.project_code ?? "",
      "Date": r.po_date,
      "Total inc GST": num(r.total_inc_gst),
    }),
  },
  {
    id: "supplier_money",
    label: "Supplier money summary",
    blurb: "Per supplier: owing now, overdue, open PO commitments, paid FY + all-time.",
    icon: Truck,
    group: "Money in / out",
    table: "v_supplier_ap_summary",
    select: "*", // proven: SupplierMoneyCard.tsx (mig 224)
    searchFields: ["legal_name", "trading_name"],
    defaultOrder: { field: "owing_inc_gst", asc: false },
    columns: [
      { key: "Supplier", label: "Supplier", type: "text" },
      { key: "Bills owing", label: "Bills owing", type: "num" },
      { key: "Owing inc GST", label: "Owing inc GST", type: "money" },
      { key: "Overdue", label: "Overdue", type: "num" },
      { key: "Overdue $", label: "Overdue $", type: "money" },
      { key: "Open PO commitments", label: "Open PO commitments", type: "money" },
      { key: "Paid FY", label: "Paid FY", type: "money" },
      { key: "Paid all-time", label: "Paid all-time", type: "money" },
      { key: "Last paid", label: "Last paid", type: "date" },
    ],
    flatten: (r) => ({
      "Supplier": r.trading_name || r.legal_name,
      "Bills owing": num(r.bills_owing),
      "Owing inc GST": num(r.owing_inc_gst),
      "Overdue": num(r.overdue_count),
      "Overdue $": num(r.overdue_inc_gst),
      "Open PO commitments": num(r.po_committed_ex_gst),
      "Paid FY": num(r.paid_fy),
      "Paid all-time": num(r.paid_all_time),
      "Last paid": r.last_paid_date,
    }),
  },
  {
    id: "receipts",
    label: "Receipt captures",
    blurb: "Field receipt snaps — who loaded what, personal-spend claims, status.",
    icon: Camera,
    group: "Money in / out",
    table: "pending_receipts",
    // proven: MyReceiptsScreen select + ApReceiptsCardsScreen capturer embed (FK hint required)
    select: `receipt_id, supplier_guess, amount_guess, notes, status, captured_at,
      claimed_personal, project_id, promoted_to_ap_invoice_id,
      capturer:employees!pending_receipts_captured_by_employee_id_fkey(first_name, last_name)`,
    searchFields: ["supplier_guess", "notes"],
    dateField: { key: "captured_at", label: "Captured" },
    statusField: {
      key: "status", label: "Status",
      options: ["new", "promoted", "rejected", "cancelled"].map(s => ({ value: s, label: s })),
    },
    defaultOrder: { field: "captured_at", asc: false },
    hydrate: (rows) => stampProjects(rows),
    columns: [
      { key: "Captured", label: "Captured", type: "datetime" },
      { key: "Loaded by", label: "Loaded by", type: "text" },
      { key: "Supplier (guess)", label: "Supplier (guess)", type: "text" },
      { key: "Amount (guess)", label: "Amount (guess)", type: "money" },
      { key: "Personal spend", label: "Personal spend", type: "bool" },
      { key: "Status", label: "Status", type: "badge" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Notes", label: "Notes", type: "text" },
    ],
    flatten: (r) => ({
      "Captured": r.captured_at,
      "Loaded by": empName(r.capturer),
      "Supplier (guess)": r.supplier_guess ?? "",
      "Amount (guess)": num(r.amount_guess),
      "Personal spend": !!r.claimed_personal,
      "Status": r.status,
      "Project": r._project?.project_code ?? "",
      "Notes": r.notes ?? "",
    }),
  },
  {
    id: "opex",
    label: "OPEX (overheads)",
    blurb: "Recurring company costs with monthly-equivalent burn.",
    icon: Repeat,
    group: "Money in / out",
    table: "v_opex_monthly",
    select: "*", // proven: OpexScreen.tsx
    searchFields: ["item_name", "category_name", "supplier_name"],
    dateField: { key: "next_due_date", label: "Next due" },
    statusField: {
      key: "frequency", label: "Frequency",
      options: ["weekly", "fortnightly", "monthly", "quarterly", "annually", "one_off"]
        .map(s => ({ value: s, label: s.replace(/_/g, " ") })),
    },
    defaultOrder: { field: "monthly_cost_ex_gst", asc: false },
    columns: [
      { key: "Item", label: "Item", type: "text" },
      { key: "Category", label: "Category", type: "text" },
      { key: "Supplier", label: "Supplier", type: "text" },
      { key: "Frequency", label: "Frequency", type: "badge" },
      { key: "Amount ex GST", label: "Amount ex GST", type: "money" },
      { key: "Monthly ex GST", label: "Monthly ex GST", type: "money" },
      { key: "Next due", label: "Next due", type: "date" },
    ],
    flatten: (r) => ({
      "Item": r.item_name,
      "Category": r.category_name ?? "",
      "Supplier": r.supplier_name ?? "",
      "Frequency": r.frequency,
      "Amount ex GST": num(r.amount_ex_gst),
      "Monthly ex GST": num(r.monthly_cost_ex_gst),
      "Next due": r.next_due_date,
    }),
  },

  // ---------------------------------------------------------------- Delivery
  {
    id: "timesheets",
    label: "Timesheet entries",
    blurb: "Hours, overtime splits, LAFHA nights and travel per person per day.",
    icon: Clock,
    group: "Delivery",
    table: "timesheet_entries",
    // proven: TeamWeekScreen fields + PayrollSummary employees:employee_id embed
    // (embed MUST be disambiguated — mirrored_from FK)
    select: `entry_id, employee_id, work_date, project_id, budget_code,
      total_hours, hours_std, hours_th, hours_dt, lafha_nights,
      travel_km, travel_hours, notes, submitted_at, approved_at,
      employees:employee_id!inner ( first_name, last_name, employment_type ),
      projects:project_id ( project_code, project_name )`,
    searchFields: [],
    searchEmbed: { table: "employees", fields: ["first_name", "last_name"] },
    dateField: { key: "work_date", label: "Work date" },
    defaultOrder: { field: "work_date", asc: false },
    columns: [
      { key: "Date", label: "Date", type: "date" },
      { key: "Employee", label: "Employee", type: "text" },
      { key: "Type", label: "Type", type: "badge" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Hours", label: "Hours", type: "num" },
      { key: "Std", label: "Std", type: "num" },
      { key: "1.5x", label: "1.5x", type: "num" },
      { key: "2x", label: "2x", type: "num" },
      { key: "LAFHA nights", label: "LAFHA nights", type: "num" },
      { key: "Travel km", label: "Travel km", type: "num" },
      { key: "Travel hrs", label: "Travel hrs", type: "num" },
      { key: "Submitted", label: "Submitted", type: "bool" },
      { key: "Approved", label: "Approved", type: "bool" },
    ],
    flatten: (r) => ({
      "Date": r.work_date,
      "Employee": empName(r.employees),
      "Type": r.employees?.employment_type ?? "",
      "Project": r.projects?.project_code ?? "",
      "Hours": num(r.total_hours),
      "Std": num(r.hours_std),
      "1.5x": num(r.hours_th),
      "2x": num(r.hours_dt),
      "LAFHA nights": num(r.lafha_nights),
      "Travel km": num(r.travel_km),
      "Travel hrs": num(r.travel_hours),
      "Submitted": !!r.submitted_at,
      "Approved": !!r.approved_at,
    }),
  },
  {
    id: "site_diaries",
    label: "Site diary entries",
    blurb: "Daily site records — crew, conditions, delays, scope done.",
    icon: BookOpen,
    group: "Delivery",
    table: "site_diary_entries",
    // proven: ProjectDiaryTab.tsx (photos embed dropped — too heavy for a report)
    select: `diary_id, project_id, entry_date, started_at, ended_at, conditions,
      delays, delay_billable, scope_done, off_site, is_job_finished, created_at,
      creator:created_by ( user_id, employees:employee_id (first_name, last_name) ),
      project:project_id ( project_code, project_name, clients ( legal_name ) )`,
    searchFields: ["scope_done", "delays"],
    dateField: { key: "entry_date", label: "Entry date" },
    defaultOrder: { field: "entry_date", asc: false },
    columns: [
      { key: "Date", label: "Date", type: "date" },
      { key: "Project", label: "Project", type: "text" },
      { key: "Client", label: "Client", type: "text" },
      { key: "By", label: "By", type: "text" },
      { key: "Weather", label: "Weather", type: "text" },
      { key: "Scope done", label: "Scope done", type: "text" },
      { key: "Delays", label: "Delays", type: "text" },
      { key: "Delay billable", label: "Delay billable", type: "bool" },
      { key: "Job finished", label: "Job finished", type: "bool" },
    ],
    flatten: (r) => ({
      "Date": r.entry_date,
      "Project": r.project?.project_code ?? "",
      "Client": r.project?.clients?.legal_name ?? "",
      "By": empName(r.creator?.employees),
      "Weather": [r.conditions?.weather, r.conditions?.temperature_c != null ? `${r.conditions.temperature_c}°C` : null]
        .filter(Boolean).join(" · "),
      "Scope done": r.scope_done ?? "",
      "Delays": r.delays ?? "",
      "Delay billable": !!r.delay_billable,
      "Job finished": !!r.is_job_finished,
    }),
  },

  // -------------------------------------------------- People & compliance
  {
    id: "employees",
    label: "Employees",
    blurb: "The team — roles, employment type, contact details.",
    icon: Users,
    group: "People & compliance",
    table: "v_employees",
    // proven: PeopleScreen.tsx embed on the view
    select: `employee_id, first_name, last_name, email, mobile, primary_role_name,
      employment_type, is_active, lafha_eligible, start_date,
      subcontractor_companies(legal_name, trading_name)`,
    searchFields: ["first_name", "last_name", "email"],
    dateField: { key: "start_date", label: "Start date" },
    statusField: {
      key: "is_active", label: "Active", isBool: true,
      options: [{ value: "true", label: "active" }, { value: "false", label: "inactive" }],
    },
    defaultOrder: { field: "last_name", asc: true },
    columns: [
      { key: "Name", label: "Name", type: "text" },
      { key: "Role", label: "Role", type: "text" },
      { key: "Type", label: "Type", type: "badge" },
      { key: "Company", label: "Company", type: "text" },
      { key: "Email", label: "Email", type: "text" },
      { key: "Mobile", label: "Mobile", type: "text" },
      { key: "Started", label: "Started", type: "date" },
      { key: "Active", label: "Active", type: "bool" },
      { key: "LAFHA", label: "LAFHA", type: "bool" },
    ],
    flatten: (r) => ({
      "Name": `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim(),
      "Role": (r.primary_role_name ?? "").replace(/_/g, " "),
      "Type": r.employment_type ?? "",
      "Company": r.subcontractor_companies
        ? (r.subcontractor_companies.trading_name || r.subcontractor_companies.legal_name)
        : "Coretel",
      "Email": r.email ?? "",
      "Mobile": r.mobile ?? "",
      "Started": r.start_date,
      "Active": !!r.is_active,
      "LAFHA": !!r.lafha_eligible,
    }),
  },
  {
    id: "licences",
    label: "Licences & tickets",
    blurb: "Every active licence with holder, expiry and verification state.",
    icon: BadgeCheck,
    group: "People & compliance",
    table: "employee_licences",
    // proven: HrLicenceDetailScreen embed; holder joined client-side (no embed exists)
    select: `licence_id, employee_id, licence_number, issue_date, expiry_date,
      state_issued, verified_at,
      licence_type:licence_types ( code, display_name, category, issuing_body )`,
    searchFields: ["licence_number"],
    dateField: { key: "expiry_date", label: "Expiry" },
    defaultOrder: { field: "expiry_date", asc: true },
    hydrate: (rows) => stampEmployees(rows),
    columns: [
      { key: "Holder", label: "Holder", type: "text" },
      { key: "Licence", label: "Licence", type: "text" },
      { key: "Category", label: "Category", type: "badge" },
      { key: "Number", label: "Number", type: "text" },
      { key: "State", label: "State", type: "text" },
      { key: "Issued", label: "Issued", type: "date" },
      { key: "Expires", label: "Expires", type: "date" },
      { key: "Verified", label: "Verified", type: "bool" },
    ],
    flatten: (r) => ({
      "Holder": empName(r._employee),
      "Licence": r.licence_type?.display_name ?? "",
      "Category": r.licence_type?.category ?? "",
      "Number": r.licence_number ?? "",
      "State": r.state_issued ?? "",
      "Issued": r.issue_date,
      "Expires": r.expiry_date,
      "Verified": !!r.verified_at,
    }),
  },
  {
    id: "subbie_compliance",
    label: "Subbie compliance",
    blurb: "Subcontractor companies — insurances, approval, compliance state.",
    icon: HardHat,
    group: "People & compliance",
    table: "v_subbie_compliance",
    select: "*", // proven: SubbieHubScreens.tsx (view def mig 221)
    searchFields: ["legal_name", "trading_name", "company_code", "abn"],
    dateField: { key: "earliest_expiry", label: "Earliest expiry" },
    statusField: {
      key: "state", label: "State",
      options: ["ok", "expiring", "expired", "no_insurance", "pending_approval"]
        .map(s => ({ value: s, label: s.replace(/_/g, " ") })),
    },
    defaultOrder: { field: "legal_name", asc: true },
    columns: [
      { key: "Company", label: "Company", type: "text" },
      { key: "State", label: "State", type: "badge" },
      { key: "Approved", label: "Approved", type: "bool" },
      { key: "PL expiry", label: "PL expiry", type: "date" },
      { key: "WC expiry", label: "WC expiry", type: "date" },
      { key: "PI expiry", label: "PI expiry", type: "date" },
      { key: "Policies", label: "Policies", type: "num" },
      { key: "Active bookings", label: "Active bookings", type: "num" },
      { key: "Contact", label: "Contact", type: "text" },
      { key: "ABN", label: "ABN", type: "text" },
    ],
    flatten: (r) => ({
      "Company": r.trading_name || r.legal_name,
      "State": r.state,
      "Approved": !!r.is_approved,
      "PL expiry": r.pl_expiry,
      "WC expiry": r.wc_expiry,
      "PI expiry": r.pi_expiry,
      "Policies": num(r.insurance_count),
      "Active bookings": num(r.active_bookings),
      "Contact": r.primary_contact_name ?? "",
      "ABN": r.abn ?? "",
    }),
  },
];

export const DATASET_BY_ID: Record<string, ReportDataset> =
  Object.fromEntries(REPORT_DATASETS.map(d => [d.id, d]));

export const DATASET_GROUPS = ["Commercial", "Money in / out", "Delivery", "People & compliance"] as const;
